import { RequestProvider } from '@/shared/providers/request.provider';
import { IScheduleEntryBody, IScheduleEntryResponse } from '../interfaces/schedule-entry.interface';
import { SCHEDULE_API, SCHEDULE_API_PARAMS, WORKING_DAYS } from '../values/schedule.values';
import { ContentSchedule, CreateScheduleBlockDto, ScheduleResponseDto, ScheduleTypeResponse, UpdateScheduleBlockDto } from '../models/class-builder.models';
import { ErrorProvider } from '@/shared/providers/errors.provider';
import { ScheduleTypeEnum } from '../enums/schedule-type.enum';

class ScheduleServiceClass {

  getScheduleByClassId(classId: number): Promise<ScheduleResponseDto[]> {
    const req = SCHEDULE_API.GET_SCHEDULE_BY_CLASS_ID
      .replace(SCHEDULE_API_PARAMS.CLASS_ID, classId.toString());

    return RequestProvider.get(req);
  }

  getScheduleByContentId(classId: number, contentId: number): Promise<ContentSchedule[]> {
    const req = SCHEDULE_API.GET_SCHEDULE_BY_CONTENT_ID
      .replace(SCHEDULE_API_PARAMS.CLASS_ID, classId.toString())
      .replace(SCHEDULE_API_PARAMS.CONTENT_ID, contentId.toString());
      
    return RequestProvider.get(req);
  }

  getScheduleType(classId: number): Promise<ScheduleTypeResponse> {
    const req = SCHEDULE_API.GET_SCHEDULE_TYPE
      .replace(SCHEDULE_API_PARAMS.CLASS_ID, classId.toString());
      
    return RequestProvider.get(req);
  }

  async addScheduleEntry(classId: number, scheduleBlock: CreateScheduleBlockDto) {
    try {
      const req = SCHEDULE_API.ADD_SCHEDULE_BLOCK
        .replace(SCHEDULE_API_PARAMS.CLASS_ID, classId.toString());

      const response = await RequestProvider.post(req, scheduleBlock);
      return response;
    } catch(error) {
      throw ErrorProvider.getErrorsOfTheRequest(error.data.errors);
    }
  }

  async updateScheduleBlock(
    scheduleBlockId: number,
    classId: number,
    updateScheduleBlockDto: UpdateScheduleBlockDto
  ) {
    try {
      const req = SCHEDULE_API.UPDATE_SCHEDULE_BLOCK
        .replace(SCHEDULE_API_PARAMS.SCHEDULE_BLOCK_ID, scheduleBlockId.toString())
        .replace(SCHEDULE_API_PARAMS.CLASS_ID, classId.toString());
      
      const response = await RequestProvider.put(req, updateScheduleBlockDto);
      return response;
    } catch(error) {
      throw ErrorProvider.getErrorsOfTheRequest(error.data.errors);
    }
  }

  async removeScheduleBlock(scheduleBlockId: number, classId: number) {
    try {
      const req = SCHEDULE_API.DELETE_SCHEDULE_BLOCK
        .replace(SCHEDULE_API_PARAMS.SCHEDULE_BLOCK_ID, scheduleBlockId.toString())
        .replace(SCHEDULE_API_PARAMS.CLASS_ID, classId.toString());

      const response = await RequestProvider.remove(req);
      return response;
    } catch(error) {
      throw ErrorProvider.getErrorsOfTheRequest(error.data.errors);
    }
  }

  async updateWorkingDays(classId: number, workingDays: WORKING_DAYS[]) {
    try {
      const req = SCHEDULE_API.UPDATE_WORKING_DAYS.replace(SCHEDULE_API_PARAMS.CLASS_ID, classId.toString());
      const formattedWorkingDays = workingDays.map((day) => WORKING_DAYS[day]);
      const response = await RequestProvider.post(req, { workingDays: formattedWorkingDays });
      return response;
    } catch(error) {
      throw ErrorProvider.getErrorsOfTheRequest(error.data.errors);
    }
  }

  async saveScheduleHours(startTime: string, endTime: string, classId: number) {
    try {
      const req = SCHEDULE_API.SCHEDULE_HOURS
        .replace(SCHEDULE_API_PARAMS.CLASS_ID, classId.toString());
    
      const response = await RequestProvider.put(req, { startTime, endTime });
      return response;
    } catch(error) {
      throw ErrorProvider.getErrorsOfTheRequest(error.data.errors);
    }
  }

  async updateScheduleTypeOptions(classId: number, scheduleType: ScheduleTypeEnum, scheduleShortDay: string = null) {
    try {
      const req = SCHEDULE_API.SCHEDULE_TYPE.replace(SCHEDULE_API_PARAMS.CLASS_ID, classId.toString());
      const response = await RequestProvider.put(req, { scheduleType, scheduleShortDay });
      return response;
    } catch(error) {
      throw ErrorProvider.getErrorsOfTheRequest(error.data.errors);
    }
  }
}

export const ScheduleService = new ScheduleServiceClass();